import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface UserCategory {
  id: string;
  user_id: string;
  category_name: string;
  category_type: 'subject' | 'category';
  is_favorite: boolean;
  created_at: string;
}

// ナガヤマのユーザーID（このユーザーが作成した科目・カテゴリは他ユーザーが削除・編集不可）
const NAGAYAMA_USER_ID = '34730814-27c6-476d-98d1-90d8c95649f1';

export const useUserCategories = (categoryType: 'subject' | 'category') => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCategories = useCallback(async () => {
    if (!user) {
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_categories')
        .select('*')
        .eq('category_type', categoryType)
        .order('category_name', { ascending: true });

      if (error) throw error;

      // 同名カテゴリの重複を除去（自分のレコードを優先）
      const nameMap = new Map<string, UserCategory>();
      for (const cat of data || []) {
        const existing = nameMap.get(cat.category_name);
        if (!existing) {
          nameMap.set(cat.category_name, cat);
        } else if (cat.user_id === user.id) {
          nameMap.set(cat.category_name, cat);
        }
      }

      const merged = Array.from(nameMap.values()).sort((a, b) => {
        if (a.is_favorite && !b.is_favorite) return -1;
        if (!a.is_favorite && b.is_favorite) return 1;
        return a.category_name.localeCompare(b.category_name, 'ja');
      });

      setCategories(merged);
    } catch (error) {
      console.error('カテゴリ取得エラー:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, categoryType]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // リアルタイム購読：他ユーザーの追加・削除を即時反映
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`user_categories_${categoryType}_realtime`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_categories',
          filter: `category_type=eq.${categoryType}`,
        },
        () => {
          fetchCategories();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, categoryType, fetchCategories]);

  // カテゴリを追加（全体共有：同名が既に存在する場合はスキップ）
  const addCategory = async (categoryName: string) => {
    if (!user) throw new Error('ログインが必要です');

    const { data: existing } = await supabase
      .from('user_categories')
      .select('id')
      .eq('category_name', categoryName)
      .eq('category_type', categoryType)
      .maybeSingle();

    if (existing) {
      throw Object.assign(new Error('この名前は既に存在します'), { code: '23505' });
    }

    try {
      const { data, error } = await supabase
        .from('user_categories')
        .insert({
          user_id: user.id,
          category_name: categoryName,
          category_type: categoryType,
          is_favorite: false,
        })
        .select()
        .single();

      if (error) throw error;
      await fetchCategories();
      return data;
    } catch (error) {
      console.error('カテゴリ追加エラー:', error);
      throw error;
    }
  };

  // お気に入りをトグル
  const toggleFavorite = async (categoryId: string, currentFavorite: boolean) => {
    if (!user) return;

    try {
      const target = categories.find((c) => c.id === categoryId);
      if (!target) return;

      // 自分が作成したカテゴリの場合のみ更新可能
      if (target.user_id === user.id) {
        const { error } = await supabase
          .from('user_categories')
          .update({ is_favorite: !currentFavorite })
          .eq('id', categoryId)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        // 他人が作成したカテゴリの場合は新規レコードを作成
        const { error } = await supabase
          .from('user_categories')
          .insert({
            user_id: user.id,
            category_name: target.category_name,
            category_type: categoryType,
            is_favorite: true,
          });
        if (error && error.code !== '23505') throw error;
      }

      await fetchCategories();
    } catch (error) {
      console.error('お気に入り更新エラー:', error);
      throw error;
    }
  };

  // カテゴリを削除（自分が追加したものだけ削除可能、ただしナガヤマ作成分は本人のみ）
  const deleteCategory = async (categoryId: string) => {
    if (!user) return;

    try {
      const target = categories.find((c) => c.id === categoryId);
      if (!target) {
        throw new Error('カテゴリが見つかりません');
      }

      // ナガヤマが作成したカテゴリはナガヤマ本人のみ削除可能
      if (target.user_id === NAGAYAMA_USER_ID && user.id !== NAGAYAMA_USER_ID) {
        throw Object.assign(new Error('このカテゴリはオーナーのみ削除できます'), { code: 'OWNER_ONLY' });
      }

      // 自分が作成したカテゴリのみ削除可能
      if (target.user_id !== user.id) {
        throw new Error('他のユーザーが作成したカテゴリは削除できません');
      }

      const { error } = await supabase
        .from('user_categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchCategories();
    } catch (error) {
      console.error('カテゴリ削除エラー:', error);
      throw error;
    }
  };

  const filteredCategories = categories.filter((cat) =>
    cat.category_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return {
    categories: filteredCategories,
    allCategories: categories,
    loading,
    searchQuery,
    setSearchQuery,
    addCategory,
    toggleFavorite,
    deleteCategory,
    refreshCategories: fetchCategories,
  };
};